"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFooterContext = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const usa_footer_1 = __importDefault(require("@uswds/uswds/js/usa-footer"));
const react_router_hash_link_1 = require("react-router-hash-link");
const uswds_react_utils_1 = require("@boozallen-uip/uswds-react-utils");
const __1 = require("../..");
const FooterContactInfo_1 = __importDefault(require("./FooterContactInfo/FooterContactInfo"));
const FooterContactItem_1 = __importDefault(require("./FooterContactItem"));
const FooterLinks_1 = __importDefault(require("./FooterLinks/FooterLinks"));
const FooterLogo_1 = __importDefault(require("./FooterLogo/FooterLogo"));
const FooterPrimaryContent_1 = __importDefault(require("./FooterPrimaryContent/FooterPrimaryContent"));
const FooterSocialLink_1 = __importDefault(require("./FooterSocialLink"));
const FooterSocialLinks_1 = __importDefault(require("./FooterSocialLinks/FooterSocialLinks"));
// Provides context for all Footer elements
const FooterContext = (0, react_1.createContext)({
    big: false,
    slim: false
});
const FooterComponent = ({ children, className, showReturnToTop = true, slim, ...props }) => {
    const containerRef = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(() => {
        const containerEl = containerRef.current;
        if (!containerEl)
            return;
        // call USWDS's `on` function to initialize the component
        usa_footer_1.default.on(containerEl);
        // on dismount, call USWDS's `off` function to cleanup the component
        return () => {
            usa_footer_1.default.off(containerEl);
        };
    }, []);
    // handle child components
    let contactInfoComponent;
    let linksComponent;
    let logoComponent;
    let primaryContentComponent;
    let socialsComponent;
    (0, uswds_react_utils_1.flattenChildren)(react_1.Children.toArray(children)).forEach((child) => {
        const componentType = (0, react_1.isValidElement)(child) ? child.type : undefined;
        switch (componentType) {
            case FooterContactInfo_1.default:
                contactInfoComponent = child;
                break;
            case FooterLinks_1.default:
                linksComponent = child;
                break;
            case FooterLogo_1.default:
                logoComponent = child;
                break;
            case FooterPrimaryContent_1.default:
                primaryContentComponent = child;
                break;
            case FooterSocialLinks_1.default:
                socialsComponent = child;
                break;
            default:
                throw new Error(`Invalid child component '${componentType}' used in Footer.`);
        }
    });
    // create primary node
    const links = linksComponent ? linksComponent.props.links : undefined;
    const hasLinks = links && links.length;
    const big = hasLinks && !!Object.getOwnPropertyDescriptor(links[0], 'header');
    let primaryNode;
    if (big) {
        primaryNode = ((0, jsx_runtime_1.jsx)(__1.Grid, { children: (0, jsx_runtime_1.jsxs)(__1.Grid.Row, { gaps: true, children: [(0, jsx_runtime_1.jsx)(__1.Grid.Col, { tablet: primaryContentComponent ? 8 : undefined, children: linksComponent && (0, react_1.cloneElement)(linksComponent) }), primaryContentComponent && ((0, jsx_runtime_1.jsx)(__1.Grid.Col, { tablet: 4, children: primaryContentComponent }))] }) }));
    }
    else if (slim) {
        primaryNode = ((0, jsx_runtime_1.jsxs)(__1.Grid.Row, { className: "usa-footer__primary-container", children: [(0, jsx_runtime_1.jsx)(__1.Grid.Col, { mobile: 8, children: linksComponent }), contactInfoComponent && ((0, jsx_runtime_1.jsx)(__1.Grid.Col, { mobile: 4, children: (0, react_1.cloneElement)(contactInfoComponent) }))] }));
    }
    else if (primaryContentComponent && hasLinks) {
        primaryNode = ((0, jsx_runtime_1.jsx)(__1.Grid, { children: (0, jsx_runtime_1.jsxs)(__1.Grid.Row, { gaps: true, children: [(0, jsx_runtime_1.jsx)(__1.Grid.Col, { tablet: 8, children: linksComponent }), (0, jsx_runtime_1.jsx)(__1.Grid.Col, { tablet: 4, children: primaryContentComponent })] }) }));
    }
    else if (primaryContentComponent) {
        primaryNode = (0, jsx_runtime_1.jsx)(__1.Grid, { children: primaryContentComponent });
    }
    else {
        primaryNode = linksComponent;
    }
    return ((0, jsx_runtime_1.jsx)(FooterContext.Provider, { value: { big: !!big, slim }, children: (0, jsx_runtime_1.jsxs)("footer", { className: (0, uswds_react_utils_1.createClassString)('usa-footer', big && 'usa-footer--big', slim && 'usa-footer--slim', className), ref: containerRef, ...props, children: [showReturnToTop && ((0, jsx_runtime_1.jsx)(__1.Grid, { className: "usa-footer__return-to-top", children: (0, jsx_runtime_1.jsx)(react_router_hash_link_1.HashLink, { smooth: true, to: "#top", children: "Return to top" }) })), primaryNode && ((0, jsx_runtime_1.jsx)("div", { className: "usa-footer__primary-section", children: primaryNode })), (0, jsx_runtime_1.jsx)("div", { className: "usa-footer__secondary-section", children: (0, jsx_runtime_1.jsx)(__1.Grid, { children: slim ? ((0, jsx_runtime_1.jsx)(__1.Grid.Row, { className: "usa-footer__logo", gaps: true, gapSize: 2, children: logoComponent })) : ((0, jsx_runtime_1.jsxs)(__1.Grid.Row, { gaps: true, children: [(0, jsx_runtime_1.jsx)(__1.Grid.Col, { className: "usa-footer__logo grid-row mobile-lg:grid-gap-2", mobile: 6, children: logoComponent }), (0, jsx_runtime_1.jsxs)(__1.Grid.Col, { className: "usa-footer__contact-links", mobile: 6, children: [socialsComponent, contactInfoComponent] })] })) }) })] }) }));
};
const useFooterContext = () => {
    const context = (0, react_1.useContext)(FooterContext);
    if (!context) {
        throw new Error('You can only utilize `useFooterContext` inside a `Footer` component');
    }
    return context;
};
exports.useFooterContext = useFooterContext;
const Footer = Object.assign(FooterComponent, {
    ContactInfo: FooterContactInfo_1.default,
    ContactItem: FooterContactItem_1.default,
    Links: FooterLinks_1.default,
    Logo: FooterLogo_1.default,
    PrimaryContent: FooterPrimaryContent_1.default,
    SocialLink: FooterSocialLink_1.default,
    SocialLinks: FooterSocialLinks_1.default
});
exports.default = Footer;
